import React, { useState, useEffect } from "react";
import axios from "axios";
import { FiChevronDown, FiChevronUp, FiEdit3, FiCamera } from "react-icons/fi";
import { DESIGNMATE } from "../../../config/endPoints";
import ImageDisplay from "./ImageDisplay";
import FullScreenView from "./FullScreenView";
import RemoveBackground from "./EditOption/RemoveBackground";
import BlurBackground from "./EditOption/BlurBackground";
import GenerateBackground from "./EditOption/GenerateBackground";
import IncreaseResolution from "./EditOption/IncreaseResolution";
import CropForeground from "./EditOption/CropForeground";
import EraseForeground from "./EditOption/EraseForeground";
import EraseElement from "./EditOption/EraseElement";
import GenerativeFill from "./EditOption/GenerativeFill";
import Popup from "../Prompt/Popup";

const WorkRoom = ({ initialImageUrl, prompt, credits, setCredits, teamId }) => {
  const [originalImage, setOriginalImage] = useState(initialImageUrl || null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [results, setResults] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [fullScreenImage, setFullScreenImage] = useState(null);
  const [compareBaseImage, setCompareBaseImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedEditOption, setSelectedEditOption] = useState(""); // For dropdown selection
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showPopup, setShowPopup] = useState(false);

  // Function to refresh credits from API
  const refreshCredits = async () => {
    try {
      const token = localStorage.getItem("authorization");
      const getOptions = {
        method: "GET",
        url: `${DESIGNMATE}/credits`,
        headers: {
          accept: "application/json",
          authorization: token,
        },
      };
      const result = await axios(getOptions);
      if (setCredits && result.data.credits !== undefined) {
        setCredits(result.data.credits);
        console.log("Credits refreshed:", result.data.credits);
      }
    } catch (error) {
      console.error("Failed to refresh credits:", error);
    }
  };

  // Define edit options
  const editOptions = [
    {
      value: "removeBackground",
      label: "Remove Background",
      component: RemoveBackground,
      icon: "🎭",
    },
    {
      value: "blurBackground",
      label: "Blur Background",
      component: BlurBackground,
      icon: "🔍",
    },
    {
      value: "generateBackground",
      label: "Generate Background",
      component: GenerateBackground,
      icon: "🖼️",
    },
    {
      value: "increaseResolution",
      label: "Increase Resolution",
      component: IncreaseResolution,
      icon: "⚡",
    },
    {
      value: "cropForeground",
      label: "Crop Foreground",
      component: CropForeground,
      icon: "✂️",
    },
    {
      value: "eraseForeground",
      label: "Erase Foreground",
      component: EraseForeground,
      icon: "🧹",
    },
    {
      value: "eraseElement",
      label: "Erase Element",
      component: EraseElement,
      icon: "🗑️",
    },
    {
      value: "generativeFill",
      label: "Generative Fill",
      component: GenerativeFill,
      icon: "🎨",
    },
  ];

  // Set initial selected image and update when initialImageUrl changes
  useEffect(() => {
    if (initialImageUrl) {
      setOriginalImage(initialImageUrl);
      setSelectedImage({ type: "original", url: initialImageUrl });
    }
  }, [initialImageUrl]);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setOriginalImage(reader.result);
        setUploadedFile(file);
        setSelectedImage({ type: "original", url: reader.result }); // <-- Add this line
      };
      reader.readAsDataURL(file);
    }
  };

  // Updated handler for edit submissions using the modular approach
  const handleEditSubmit = async ({ editType, options, apiFunction }) => {
    if (!selectedImage) {
      setError("Please select an image to edit");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Determine which image to use based on selection
      let imageToProcess;
      
      if (selectedImage.type === "original" && uploadedFile) {
        // If it's the original image and we have the uploaded file, use the file object
        imageToProcess = uploadedFile;
        console.log("Using uploaded file object for processing");
      } else if (selectedImage.type === "original") {
        // If it's the original image but no uploaded file (perhaps a URL was set directly)
        imageToProcess = originalImage;
        console.log("Using original image URL for processing");
      } else {
        // For any edited image, use its URL
        imageToProcess = results.find((r) => r.id === selectedImage.id).url;
        console.log("Using selected result image for processing");
      }
      
      // Use the provided API function
      const response = await apiFunction(imageToProcess, options);
      
      console.log("API response received:", response);
      
      // Handle different response formats according to backend documentation
      if (response.urls && Array.isArray(response.urls)) {
        // Generative Fill response format: { urls: [...], seeds: [...] }
        const newResults = response.urls.map((url, index) => ({
          id: Date.now() + index,
          url: url,
          editType,
          options,
          baseImageId: selectedImage.id || "original",
          seed: response.seeds ? response.seeds[index] : undefined
        }));

        setResults([...results, ...newResults]);
        
        // Refresh credits after successful result
        await refreshCredits();
      } else if (response.result_url) {
        // Single result response format: { result_url: "..." }
        const newResult = {
          id: Date.now(),
          url: response.result_url,
          editType,
          options,
          baseImageId: selectedImage.id || "original",
        };

        setResults([...results, newResult]);
        
        // Refresh credits after successful result
        await refreshCredits();
      } else if (response.result && Array.isArray(response.result)) {
        // Legacy format support
        const newResults = response.result.map(result => ({
          id: result.id,
          url: result.url,
          editType,
          options,
          baseImageId: selectedImage.id || "original",
        }));

        setResults([...results, ...newResults]);
        
        // Refresh credits after successful result
        await refreshCredits();
      } else {
        console.error("Unexpected response format:", response);
        throw new Error("Invalid response format from API");
      }
    } catch (err) {
      console.error("Processing error:", err);
      setError(err.response?.data?.message || `Failed to ${editType}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Open full screen view with proper base image for comparison
  const openFullScreen = (image, baseImageId) => {
    setFullScreenImage(image);
    if (baseImageId) {
      const base =
        baseImageId === "original"
          ? originalImage
          : results.find((r) => r.id === baseImageId)?.url;
      setCompareBaseImage(base);
    } else {
      // If it's the original image being viewed, no comparison needed
      setCompareBaseImage(null);
    }
    setIsFullScreen(true);
  };

  const handleImageSelect = (image) => {
    setSelectedImage(image);
  };

  // Handle dropdown toggling
  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const selectOption = (optionValue) => {
    setSelectedEditOption(optionValue);
    setIsDropdownOpen(false);
  };

  // Get the current component based on selected option
  const getCurrentComponent = () => {
    if (!selectedEditOption) return null;
    const option = editOptions.find((opt) => opt.value === selectedEditOption);
    if (!option) return null;
    
    const Component = option.component;
    
    // Pass additional props to GenerateBackground and GenerativeFill components
    const commonProps = {
      onSubmit: handleEditSubmit,
      isLoading: isLoading,
      selectedImage: selectedImage
    };
    
    if (selectedEditOption === "generateBackground" || selectedEditOption === "generativeFill") {
      return (
        <Component 
          {...commonProps}
          credits={credits}
          setCredits={setCredits}
          setShowPopup={setShowPopup}
        />
      );
    }
    
    return (
      <Component 
        {...commonProps}
      />
    );
  };

  // Get the current option label
  const getCurrentOptionLabel = () => {
    if (!selectedEditOption) return "Select Edit Option";
    const option = editOptions.find((opt) => opt.value === selectedEditOption);
    return option ? option.label : "Select Edit Option";
  };

  if (!originalImage) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "70vh",
          background: "linear-gradient(to bottom, #f7f9fc, #e9eef7)",
        }}
      >
        <div
          style={{
            textAlign: "center",
            padding: "2.5rem",
            borderRadius: "8px",
            backgroundColor: "white",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
            width: "100%",
            maxWidth: "400px",
          }}
        >
          <FiCamera
            style={{
              fontSize: "3rem",
              color: "#4D7CFE",
              margin: "0 auto 1.5rem",
            }}
          />
          <h2
            style={{
              fontSize: "1.5rem",
              fontWeight: "600",
              marginBottom: "1.5rem",
              color: "#333",
            }}
          >
            Upload an Image to Edit
          </h2>
          <label
            style={{
              display: "inline-block",
              padding: "0.75rem 1.5rem",
              backgroundColor: "#4D7CFE",
              color: "white",
              borderRadius: "4px",
              cursor: "pointer",
              fontWeight: "500",
              boxShadow: "0 2px 4px rgba(77, 124, 254, 0.3)",
              transition: "all 0.2s ease",
            }}
          >
            Choose Image
            <input
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              style={{ display: "none" }}
            />
          </label>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        height: "100%",
        minHeight: "80vh",
        backgroundColor: "#f7f9fc",
      }}
    >
      {/* Left sidebar - Edit options */}
      <div
        style={{
          width: "30%",
          padding: "1.5rem",
          backgroundColor: "white",
          borderRight: "1px solid #eaedf3",
          boxShadow: "2px 0 10px rgba(0,0,0,0.03)",
          display: "flex",
          flexDirection: "column",
        }}
      >

{prompt && (
          <div
            style={{
              marginBottom: "1.5rem",
              padding: "1rem",
              backgroundColor: "#f0f5ff",
              borderRadius: "6px",
              border: "1px solid #e0e9ff",
            }}
          >
            <h4
              style={{
                margin: "0 0 0.5rem 0",
                fontSize: "0.9rem",
                fontWeight: "600",
                color: "#4D7CFE",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Original Prompt
            </h4>
            <p
              style={{
                fontSize: "0.95rem",
                margin: 0,
                lineHeight: "1.5",
                color: "#555",
              }}
            >
              {prompt}
            </p>
          </div>
        )}

        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "1.5rem",
          }}
        >
          <FiEdit3
            style={{
              fontSize: "1.4rem",
              color: "#4D7CFE",
              marginRight: "0.75rem",
            }}
          />
          <h3
            style={{
              margin: 0,
              fontSize: "1.3rem",
              fontWeight: "600",
              color: "#333",
            }}
          >
            Edit Image
          </h3>
        </div>

        

        {/* Custom Dropdown */}
        <div style={{ marginBottom: "1.5rem", position: "relative" }}>
          {/* <label
            style={{
              display: "block",
              marginBottom: "0.75rem",
              fontSize: "0.9rem",
              fontWeight: "600",
              color: "#555",
            }}
          >
            Select Edit Option:
          </label> */}

          <div style={{ position: "relative" }}>
            <div
              onClick={toggleDropdown}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                padding: "0.75rem 1rem",
                borderRadius: "6px",
                border: "1px solid #dde3ed",
                backgroundColor: "white",
                cursor: "pointer",
                boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                transition: "all 0.2s ease",
                color: selectedEditOption ? "#333" : "#8e979c",
              }}
            >
              <span>{getCurrentOptionLabel()}</span>
              {isDropdownOpen ? (
                <FiChevronUp style={{ color: "#4D7CFE" }} />
              ) : (
                <FiChevronDown style={{ color: "#8e979c" }} />
              )}
            </div>

            {isDropdownOpen && (
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 5px)",
                  left: 0,
                  width: "100%",
                  backgroundColor: "white",
                  borderRadius: "6px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  zIndex: 100,
                  maxHeight: "300px",
                  overflowY: "auto",
                  border: "1px solid #eaedf3",
                }}
              >
                {editOptions.map((option) => (
                  <div
                    key={option.value}
                    onClick={() => selectOption(option.value)}
                    style={{
                      padding: "0.75rem 1rem",
                      cursor: "pointer",
                      borderBottom: "1px solid #f0f2f5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      backgroundColor:
                        selectedEditOption === option.value
                          ? "#f0f5ff"
                          : "transparent",
                      transition: "background-color 0.2s ease",
                      color:
                        selectedEditOption === option.value ? "#4D7CFE" : "#333",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center" }}>
                    <span style={{ marginRight: "0.6rem", fontSize: "1.1rem" }}>
                      {option.icon}
                    </span>
                    {option.label}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Show the selected component */}
        <div
          style={{
            backgroundColor: "white",
            padding: "1.25rem",
            borderRadius: "8px",
            border: "1px solid #eaedf3",
            boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
            marginTop: "0.5rem",
            flexGrow: 1,
            overflowY: "auto",
          }}
        >
          {getCurrentComponent() || (
            <div
              style={{
                textAlign: "center",
                color: "#8e979c",
                padding: "2rem 0",
              }}
            >
              <p>
                Select an edit option from the dropdown above to get started
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Right side - Image display */}
      <div
        style={{
          width: "70%",
          padding: "1.5rem",
          overflowY: "auto",
          backgroundColor: "#f7f9fc",
        }}
      >
        <ImageDisplay
          originalImage={originalImage}
          results={results}
          selectedImage={selectedImage}
          onSelectImage={handleImageSelect}
          onOpenFullScreen={openFullScreen}
        />
      </div>

      {/* Full screen view */}
      {isFullScreen && (
        <FullScreenView
          image={fullScreenImage}
          baseImage={compareBaseImage}
          onClose={() => setIsFullScreen(false)}
        />
      )}

      {/* Error message */}
      {error && (
        <div
          style={{
            position: "fixed",
            bottom: "1.5rem",
            right: "1.5rem",
            backgroundColor: "#ff4757",
            color: "white",
            padding: "0.75rem 1.25rem",
            borderRadius: "6px",
            boxShadow: "0 4px 12px rgba(255, 71, 87, 0.3)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            maxWidth: "400px",
            animation: "slideIn 0.3s ease-out",
          }}
        >
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            style={{
              marginLeft: "1rem",
              background: "none",
              border: "none",
              color: "white",
              fontSize: "1.2rem",
              cursor: "pointer",
              padding: "0.25rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Popup for insufficient credits */}
      <Popup
        showPopup={showPopup}
        setShowPopup={setShowPopup}
        popupText="You've exhausted your credits. Kindly purchase more credits."
      />
    </div>
  );
};

export default WorkRoom;
